'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { ImageComponent } from '../Image';
import { AnalyticsEventName } from '@/api/analytics';
import { User } from '@/api/auth';
import { GameV1 } from '@/api/games';
import Badge from '@/components/Badge';
import { Button } from '@/components/buttons/Button';
import { ButtonHeartComponent } from '@/components/GameItem/components/ButtonHeartComponent';
import { getAvailableRecommendedCurrency } from '@/components/GameItem/helpers';
import { getGameCover } from '@/helpers/cdn';
import { isSvg } from '@/helpers/common';
import { snakify } from '@/helpers/common';
import {
  amountWithCurrencySymbol,
  currencyCodeByLocation,
  getCurrencyName,
} from '@/helpers/currency';
import { GameItemMode, MediaType } from '@/helpers/enums';
import { isFavoriteGame } from '@/helpers/game';
import { Locale } from '@/helpers/languages';
import { Modals } from '@/helpers/modals';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useAnalytics } from '@/hooks/analytics';
import useEventListener from '@/hooks/eventListener';
import { useNavigation } from '@/hooks/navigation';
import { isPlayerFieldsFilled, PlayerFields } from '@/hooks/profile';
import { useCurrentMediaType } from '@/hooks/responsive';
import { MAP_LOCALE_TO_CURRENCY } from '@/modules/shared';
import { useCommonStore, useModalsStore } from '@/store';
import { blurImagePlaceholder } from '@/theme/placeholders';

import {
  GameItemSize,
  ScGameItemWrapper,
  ScGamesGridItem,
  ScGamesGridItemProps,
  ScInfoBlock,
  ScPrimeGame,
} from './styled';

export const defaultGameSizesConfig: GameSizeConfig = {
  desktopL: GameItemSize.L,
  desktop: GameItemSize.M,
  tablet: GameItemSize.M,
  mobile: GameItemSize.M,
  mobileS: GameItemSize.S,
};

const RANKING_COUNT = 50;

export interface GameSizeConfig {
  desktopL: GameItemSize;
  desktop: GameItemSize;
  tablet: GameItemSize;
  mobile: GameItemSize;
  mobileS: GameItemSize;
}

type GameItemProps = Omit<ScGamesGridItemProps, 'image'> & {
  isLocked?: boolean;
  mode?: GameItemMode;
  gameSizesConfig?: GameSizeConfig;
  openDemoOnPlay?: boolean;
};

export const GameItem: FC<GameV1 & GameItemProps> = ({
  id,
  fullPath,
  title,
  real,
  provider,
  collections,
  isLocked,
  mode = GameItemMode.grid,
  primeGame = false,
  gameSizesConfig = defaultGameSizesConfig,
  colorMode,
  demo,
  openDemoOnPlay,
}) => {
  const currentMediaType = useCurrentMediaType();
  const { navigate } = useNavigation();
  const { sendEventToAnalytics } = useAnalytics();
  const { lockIcon: LockIcon, playIcon: PlayIcon } = useTheme();
  const pathname = usePathname();
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const {
    isMobileDevice,
    isAuthenticated,
    user,
    wallets,
    covers,
    currencies,
    countryCode,
    favoriteGames,
    playerFields,
  } = useCommonStore(
    useShallow((state) => ({
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      wallets: state.wallets,
      covers: state.covers,
      currencies: state.currencies,
      countryCode: state.countryCode,
      favoriteGames: state.favoriteGames,
      playerFields: state.playerFields,
    }))
  );

  const [notFoundImage, setNotFoundImage] = useState(false);
  const open = useModalsStore.use.open();

  const ref = useRef<any>(null);

  const [isVisibleInfo, setIsVisibleInfo] = useState(false);

  const emptyFields = useMemo(() => {
    if (isAuthenticated && user && playerFields) {
      const { contexts } = playerFields;
      const { missingFields } = isPlayerFieldsFilled(
        // @ts-ignore
        snakify(user),
        (contexts[provider] as (keyof User)[]) || []
      );
      return missingFields;
    }
    return [];
  }, [user, isAuthenticated, playerFields]);

  const isMissingGender = emptyFields.includes(PlayerFields.GENDER);

  const isDemoGame = useMemo(
    () => !!(real[DEMO_CURRENCY] || demo),
    [real, demo]
  );

  useEventListener(
    'mouseenter',
    () => {
      !isMobileDevice && setIsVisibleInfo(true);
    },
    ref
  );

  useEventListener(
    'mouseleave',
    () => {
      !isMobileDevice && setIsVisibleInfo(false);
    },
    ref
  );

  useEventListener('click', (event) => {
    if (!isMobileDevice) return;

    if (!ref.current?.contains(event.target)) {
      setIsVisibleInfo(false);
    } else {
      setIsVisibleInfo(true);
    }
  });

  const hasCurrency = useMemo(() => {
    if (user?.currency == null) return false;
    return Object.keys(real).includes(user.currency);
  }, [real, user?.currency]);

  const availableCurrency = useMemo(() => {
    return getAvailableRecommendedCurrency(real, wallets);
  }, [real, wallets]);

  const coverImage = useMemo(() => {
    return covers?.[id]?.cover?.url || getGameCover(id);
  }, [covers, id]);

  const gradient = useMemo(() => covers?.[id]?.gradientColor, [covers, id]);

  const handleImageError = () => {
    setNotFoundImage(true);
  };

  const [isNew, isTop, isTrending] = useMemo(
    () => [
      collections.new < RANKING_COUNT,
      collections.top < RANKING_COUNT,
      collections.trending < RANKING_COUNT,
    ],
    [collections.new, collections.top, collections.trending]
  );

  const handleOpenDemoGame = useCallback(
    (id: string) => {
      useCommonStore.setState({ gameOpenBackUrl: pathname });

      if (isMissingGender) {
        open(Modals.GenderSelect, {
          customOnClose: () => {
            navigate(`/game/${fullPath}?demo=true`);
          },
        });
        return;
      }

      navigate(`/game/${fullPath}?demo=true`);
      sendEventToAnalytics({
        name: AnalyticsEventName.GAME_DEMO_GAME,
        dynamicParams: {
          game_name: id.toLowerCase().split(' ').join('_'),
        },
      });
    },
    [fullPath, navigate, pathname, sendEventToAnalytics, isMissingGender, open]
  );

  const handleRealGameEvent = (id: string) => {
    sendEventToAnalytics({
      name: AnalyticsEventName.GAME_REAL_GAME,
      dynamicParams: {
        game_name: id.toLowerCase().split(' ').join('_'),
      },
    });
  };

  const handleOpenRealGame = useCallback(() => {
    if (openDemoOnPlay && isDemoGame) return handleOpenDemoGame(id);

    if (isAuthenticated) {
      useCommonStore.setState({ gameOpenBackUrl: pathname });
      if (isMissingGender) {
        open(Modals.GenderSelect, {
          customOnClose: () => {
            navigate(`/game/${fullPath}`);
          },
        });
        return;
      }

      navigate(`/game/${fullPath}`);
    } else {
      open(Modals.Login);
    }
  }, [
    openDemoOnPlay,
    isDemoGame,
    handleOpenDemoGame,
    id,
    isAuthenticated,
    pathname,
    navigate,
    fullPath,
    open,
    isMissingGender,
  ]);

  const jackpot = useMemo(() => {
    const currencyCode = (() => {
      if (user?.currency == null) {
        return currencyCodeByLocation({ currencies, countryCode });
      }
      return user.currency;
    })();

    return real[currencyCode]?.jackpot;
  }, [user, real, currencies, countryCode]);

  const isFavorite = useMemo(
    () => isFavoriteGame(real, favoriteGames),
    [real, favoriteGames]
  );

  const gameSize = useMemo(() => {
    switch (currentMediaType) {
      case MediaType.DESKTOP_L:
        return gameSizesConfig?.desktopL;
      case MediaType.DESKTOP:
        return gameSizesConfig?.desktop;
      case MediaType.TABLET:
        return gameSizesConfig?.tablet;
      case MediaType.MOBILE:
        return gameSizesConfig?.mobile;
      case MediaType.MOBILE_S:
      default:
        return gameSizesConfig?.mobileS;
    }
  }, [currentMediaType, gameSizesConfig]);

  const demoPadding = useMemo(() => {
    switch (gameSize) {
      case GameItemSize.S:
      case GameItemSize.XS:
      case GameItemSize.XXS:
        return 16;
      case GameItemSize.L:
      case GameItemSize.M:
      default:
        return 12;
    }
  }, [gameSize]);

  const prize = useMemo(() => {
    const currency = MAP_LOCALE_TO_CURRENCY[language as Locale];
    const jackpot = real[currency]?.jackpot;
    if (!jackpot) return null;
    const { amount } = getCurrencyCentsAndCode(jackpot, currency, currencies);
    return amountWithCurrencySymbol(getCurrencyName(currency), amount);
  }, [real, language]);

  const gameGridItemMode = useMemo(() => {
    return mode === GameItemMode.random
      ? GameItemMode.random
      : primeGame
        ? GameItemMode.grid
        : prize && jackpot && gameSize !== GameItemSize.XXS
          ? GameItemMode.jackpot
          : mode;
  }, [gameSize, jackpot, mode, primeGame, prize]);

  const handlePrimeGameButtonClick = useCallback(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.LOBBY__PRIME_GAME__BUTTON__CLICK,
    });
    handleOpenRealGame();
  }, [handleOpenRealGame, sendEventToAnalytics]);

  return (
    <ScGameItemWrapper ref={ref} primeGame={primeGame}>
      <ScGamesGridItem
        isAuthenticated={isAuthenticated}
        gradientColor={gradient}
        isLocked={isLocked}
        isVisibleInfo={isVisibleInfo}
        primeGame={primeGame}
        size={gameSize}
        isFavorite={isFavorite}
        mode={gameGridItemMode}
        colorMode={colorMode}
      >
        {(gameSize !== GameItemSize.XXS || mode === GameItemMode.random) &&
          isAuthenticated &&
          !isLocked &&
          hasCurrency && (
            <ButtonHeartComponent
              real={real}
              size={gameSize}
              isVisible={isVisibleInfo}
            />
          )}
        {jackpot &&
          (gameSize !== GameItemSize.XXS || mode === GameItemMode.random) &&
          gameGridItemMode === GameItemMode.jackpot && (
            <div className="jackpot-gradient" />
          )}
        {!notFoundImage &&
          (isSvg(coverImage) ? (
            <object
              aria-label="cover"
              className="image"
              data={coverImage}
              onError={handleImageError}
            />
          ) : (
            <ImageComponent
              alt="cover"
              className="image"
              src={coverImage}
              onError={handleImageError}
              loading="lazy"
              width={300}
              height={300}
              placeholder="blur"
              blurDataURL={blurImagePlaceholder}
            />
          ))}
        {(gameSize !== GameItemSize.XXS || mode === GameItemMode.random) &&
          (isNew || isTop || jackpot || isTrending) && (
            <div className="badges">
              {isNew && <Badge type="new" />}
              {isTop && <Badge type="top" />}
              {jackpot && <Badge type="jackpot" />}
              {isTrending && <Badge type="trending" />}
            </div>
          )}
        {(((isAuthenticated && !hasCurrency) || isLocked) && (
          <div className="currency-block">
            <div className="lock-container">
              <LockIcon />
            </div>
            {!isLocked &&
              (gameSize !== GameItemSize.XXS ||
                mode === GameItemMode.random) && (
                <p className={language}>
                  <Trans
                    ns="common"
                    i18nKey="noCurrency"
                    values={{ currency: availableCurrency }}
                    components={{ 2: <strong /> }}
                  >
                    This game is in your reach with {`<2>{{currency}}</2>`}{' '}
                    currency
                  </Trans>
                </p>
              )}
          </div>
        )) || (
          <AnimatePresence>
            {isVisibleInfo && (
              <ScInfoBlock
                primeGame={primeGame}
                size={gameSize}
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.3 } }}
                exit={{ opacity: 0, transition: { duration: 0.3 } }}
              >
                {gameSize === GameItemSize.XXS &&
                mode !== GameItemMode.random ? (
                  <button
                    type="button"
                    onClick={handleOpenRealGame}
                    className="play-btn__small"
                  />
                ) : (
                  <>
                    <div className="title">{title}</div>
                    <div className="play-btn_container">
                      <Button
                        secondary
                        small
                        onClick={() => {
                          handleOpenRealGame();
                          handleRealGameEvent(id);
                        }}
                        wide
                        autoPaddingHorizontal
                        height="100%"
                      >
                        <span className="play-btn-content">
                          <PlayIcon />
                          <span>{t('button.play')}</span>
                        </span>
                      </Button>
                    </div>
                    {isDemoGame && (
                      <div className="demo-btn_container">
                        <Button
                          xSmall
                          autoPaddingHorizontal
                          grayTransparentBg
                          onClick={() => handleOpenDemoGame(id)}
                          height="100%"
                          paddingX={demoPadding}
                        >
                          <span className="demo-btn_content">{t('demo')}</span>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </ScInfoBlock>
            )}
          </AnimatePresence>
        )}
        {(hasCurrency || !isAuthenticated) &&
          !isVisibleInfo &&
          mode !== GameItemMode.random &&
          gameSize !== GameItemSize.XXS && (
            <div className="provider">
              <div className="provider-name">{provider}</div>
              {prize &&
                jackpot &&
                gameGridItemMode === GameItemMode.jackpot && (
                  <div className="prize">
                    <ImageComponent
                      className="cup-icon"
                      src="/jackpot/diamond.webp"
                      width={160}
                      height={160}
                      alt="diamond"
                    />
                    {prize}
                  </div>
                )}
            </div>
          )}
      </ScGamesGridItem>
      {primeGame && (
        <ScPrimeGame>
          <p>{title}</p>
          <Button
            primary
            small
            onClick={handlePrimeGameButtonClick}
            autoPaddingHorizontal
            className={language.toLowerCase()}
          >
            <span className="play-btn-content">
              <PlayIcon />
              <span>{t('button.playNow')}</span>
            </span>
          </Button>
        </ScPrimeGame>
      )}
    </ScGameItemWrapper>
  );
};
